import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in";

interface RelatedPost {
  slug: string;
  title: string;
  image: string | null;
  published_at: string | null;
  created_at: string | null;
  reading_time_mins: number | null;
}

interface RelatedPostsProps {
  categoryId: number;
  excludeSlug: string;
}

const getImageUrl = (image: string | null): string | null => {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  if (image.startsWith("/")) {
    if (typeof window !== "undefined" && window.location.host.endsWith("axoix.com")) return image;
    return `${API_BASE}${image}`;
  }
  return `${API_BASE}/api/chorus/blog/ai/image/${image}`;
};

const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return "";
  return new Date(isoDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const styles = `
  .related-posts { margin-top: 60px; }
  .related-posts-heading { margin-bottom: 24px; }
  .related-posts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 767px) { .related-posts-grid { grid-template-columns: 1fr; } }
  .related-post-card { border-radius: 12px; overflow: hidden; display: block; }
  .related-post-card img {
    width: 100%; height: 140px; object-fit: cover; border-radius: 12px;
    border: 1px solid rgba(0,255,151,0.15); margin-bottom: 12px;
  }
  .related-post-card-placeholder {
    width: 100%; height: 140px; border-radius: 12px; margin-bottom: 12px;
    background: linear-gradient(135deg, rgba(0,255,151,0.08), rgba(0,0,0,0.3));
    border: 1px solid rgba(0,255,151,0.15);
    display: flex; align-items: center; justify-content: center;
    font-size: 28px; color: rgba(0,255,151,0.3);
  }
  .related-post-title { font-size: 15px; line-height: 1.4; margin-bottom: 6px; color: var(--color-heading, #fff); }
  .related-post-meta { font-size: 12px; opacity: 0.55; }
`;

const RelatedPosts: React.FC<RelatedPostsProps> = ({ categoryId, excludeSlug }) => {
  const [posts, setPosts] = useState<RelatedPost[]>([]);

  useEffect(() => {
    fetch(`${API_BASE}/api/chorus/blog/posts?site=axoix&status=published&category_id=${categoryId}&limit=4`)
      .then((res) => res.json())
      .then((data) => {
        const list: RelatedPost[] = (data.posts || []).filter((p: RelatedPost) => p.slug !== excludeSlug);
        setPosts(list.slice(0, 3));
      })
      .catch(() => {});
  }, [categoryId, excludeSlug]);

  if (posts.length === 0) return null;

  return (
    <div className="related-posts">
      <style>{styles}</style>
      <h3 className="related-posts-heading">Related Articles</h3>
      <div className="related-posts-grid">
        {posts.map((post) => {
          const imgUrl = getImageUrl(post.image);
          return (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="related-post-card">
              {imgUrl ? (
                <img src={imgUrl} alt={post.title} />
              ) : (
                <div className="related-post-card-placeholder">
                  <i className="fas fa-newspaper"></i>
                </div>
              )}
              <h4 className="related-post-title">{post.title}</h4>
              <span className="related-post-meta">
                {formatDate(post.published_at || post.created_at)}
                {post.reading_time_mins ? ` · ${post.reading_time_mins} min read` : ""}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedPosts;
